import { IComponentOptions, IScope } from "angular";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../../records/utils";
import { RecordFileTemplateOptions } from "../../../../../../records/forms/formly/types";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";
import { MachineConfigurationViewThreeRivers } from "../../../../models/machineConfiguration";
import { cloneDeep } from "lodash";
import { IFieldArray } from "AngularFormly";
import * as lookupNames from "../../../../configs/lookup";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName } from "../../../../../shared/lookups";

interface IMultipleMoveFormModel {
  MachineChanges: IMachineMoveRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

interface IMachineMoveRequest {
  MachineId: number;
  SlotNumber?: string;
  Section?: string;
  Bank?: string;
  Position?: string;
  MachineBankConfigId?: number;
  MachineBankConfig?: any;
  MachineAreaId?: number;
  MachineArea?: any;
}

export class MachinesMoveViewComponentController {
  /* bindings */
  machineConfigurations!: MachineConfigurationViewThreeRivers[];
  /* end bindings */

  model!: IMultipleMoveFormModel;
  machineInfo!: {
    machineConfigurations: MachineConfigurationViewThreeRivers[];
  };
  formFields!: IFieldArray;

  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    "$scope",
    RecordsUtilsServiceName,
    vsLookupManagementServiceName,
  ];
  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
    private readonly lookupManagementService: LookupManagementService
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private initializeFormFields() {
    const fileTemplateOptions: RecordFileTemplateOptions = {
      allowMultiple: true,
      label: "Files",
    };

    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Change Date",
            required: true,
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            queryUrl: "/api/properties",
            getDefaultItem: (model: any) => model.Property,
            label: "Property",
            key: "PropertyId",
            required: true,
          }),
        ],
        4
      ),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        name: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        displayNameSingular: "Machine",
        repeaterFields: [
          {
            className: "row",
            fieldGroup: [
              fieldHelpers.simpleReadonlyField({
                key: "MachineId",
                className: "col-xs-3",
                label: "Serial Number",
                formatters: [
                  (machineId) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        <number>machineId
                      );
                    return machineConfigurationView?.SerialNumber;
                  },
                ],
              }),
              fieldHelpers.simpleStringField({
                key: "SlotNumber",
                className: "col-xs-3",
                label: "Asset Number",
                required: true,
              }),
              this.fieldConfigurationHelperService.lookupField(
                this.lookupManagementService,
                {
                  key: "MachineAreaId",
                  objectName: lookupNames.areaName,
                  required: true,
                  className: "col-xs-3",
                  expressionProperties: {
                    "templateOptions.helpText": (
                      $viewValue,
                      $modelValue,
                      $scope
                    ) => {
                      const machineConfigurationView =
                        this.getMachineConfigurationViewFromMachineId(
                          $scope.model?.MachineId
                        );
                      const area = <any>(
                        machineConfigurationView?.MachineAreaName
                      );
                      return area ? `Previous: ${area}` : undefined;
                    },
                  },
                  hideAddButton: true,
                }
              ),
              {
                key: "Section",
                name: "Section",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Section",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const section = machineConfigurationView?.Section;
                    return section ? `Previous: ${section}` : undefined;
                  },
                },
              },
            ],
          },
          {
            className: "row",
            fieldGroup: [
              {
                key: "Bank",
                name: "Bank",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Bank",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const bank = machineConfigurationView?.Bank;
                    return bank ? `Previous: ${bank}` : undefined;
                  },
                },
              },
              {
                key: "Position",
                name: "Position",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Position",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const slot = machineConfigurationView?.Position;
                    return slot ? `Previous: ${slot}` : undefined;
                  },
                },
              },
              this.fieldConfigurationHelperService.lookupField(
                this.lookupManagementService,
                {
                  key: "MachineBankConfigId",
                  label: "Bank Config",
                  objectName: lookupNames.machineBankConfigName,
                  required: true,
                  className: "col-xs-3",
                  expressionProperties: {
                    "templateOptions.helpText": (
                      $viewValue,
                      $modelValue,
                      $scope
                    ) => {
                      const machineConfigurationView =
                        this.getMachineConfigurationViewFromMachineId(
                          $scope.model?.MachineId
                        );
                      const bank =
                        machineConfigurationView?.MachineBankConfigName;
                      return bank ? `Previous: ${bank}` : undefined;
                    },
                  },
                  hideAddButton: true,
                }
              ),
              {
                key: "copyDown",
                name: "copyDown",
                className: "col-xs-3",
                templateOptions: {
                  label: " ",
                },
                template: `
            <div>
              <div class="pull-right">
                <div class="btn-group">
                  <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
                    <i class="glyphicon glyphicon-arrow-down"></i>
                  </div>
                </div>
              </div>
            </div>
            `,
                wrapper: ["bootstrapLabel", "bootstrapHasError"],
              },
            ],
          },
        ],
        showHeader: true,
      }),
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: fileTemplateOptions,
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Files: [],
      MachineChanges: (this.machineConfigurations ?? []).map((mc) => {
        return {
          MachineId: mc.MachineId,
          SlotNumber: <string | undefined>mc.SlotNumber,
          Section: undefined,
          Bank: undefined,
          Slot: undefined,
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields();
  }

  //TODO find index of passed in model and then update all rows after that index with the same MachineBankId
  copyDown(modelClicked: IMachineMoveRequest) {
    const machineMoves = this.model.MachineChanges;
    const copyIndex = machineMoves.indexOf(modelClicked);
    for (let i = copyIndex + 1; i < machineMoves.length; i++) {
      const newModel: IMachineMoveRequest = {
        MachineId: machineMoves[i].MachineId,
        SlotNumber: modelClicked.SlotNumber,
        Section: modelClicked.Section,
        Bank: modelClicked.Bank,
        MachineArea: modelClicked.MachineArea,
        MachineAreaId: modelClicked.MachineAreaId,
        MachineBankConfig: modelClicked.MachineBankConfig,
        MachineBankConfigId: modelClicked.MachineBankConfigId,
      };
      this.recordUtils.assignId(newModel);
      machineMoves.splice(i, 1, newModel);
    }
  }

  async submit(formViewController: FormViewController): Promise<void> {
    await this.formViewService.submitForm({
      url: `/api/machines/move`,
      formObject: this.model,
      formViewController,
      method: "post",
    });

    await this.$state.go("^.grid", undefined, { reload: true });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid", undefined, { reload: true });
  }

  private getMachineConfigurationViewFromMachineId(machineId: number) {
    return this.machineInfo.machineConfigurations.find(
      (mc) => mc.MachineId === machineId
    );
  }
}
